// MIT License - Copyright (c) 2026 Doug McNall - See .kiro/steering/general/development-standards.md for full license

/**
 * WarpTravelManager - Handles warp travel mechanics
 * 
 * Extracted from NavigationPanel to improve maintainability and testability.
 * Manages:
 * - Fuel consumption calculations
 * - Travel progress tracking
 * - Engine damage at high warp factors
 * - Fuel depletion and stranded scenarios
 * - Alternative route suggestions
 */

import * as NavUtils from './NavigationUtils';
import GameConfig from '../../config/GameConfig';
import { getDifficultyMultipliers } from '../DifficultyUtils';

/**
 * Start warp timer with fuel checks and alternative route suggestions
 * 
 * @param {Object} params - Configuration object
 * @param {number} params.travelTime - Travel time in seconds
 * @param {Object} params.originLocation - Starting location
 * @param {Object} params.currentLocation - Current location (may differ from origin)
 * @param {Object} params.selectedSystem - Destination system
 * @param {Object} params.distancePositions - Position lookup for all systems
 * @param {number} params.selectedWarpFactor - Selected warp factor (1-9)
 * @param {Function} params.getDefaultWarpFactor - Function to get default warp factor
 * @param {Function} params.getFuelEfficiencyBonus - Function to get fuel efficiency bonus
 * @param {number} params.currentFuel - Current fuel percentage
 * @param {number} params.maxFuel - Maximum fuel capacity
 * @param {Array} params.starSystems - All available star systems
 * @param {Function} params.calculateDistanceBetweenSystems - Distance calculation function
 * @param {string} params.navigationMode - Current navigation mode
 * @param {Object} params.callbacks - Callback functions for state updates
 * @returns {Object|null} Returns null if travel starts, or event object if insufficient fuel
 */
export const startWarpTimer = (params) => {
  const {
    travelTime,
    originLocation,
    currentLocation,
    selectedSystem,
    distancePositions,
    selectedWarpFactor,
    getDefaultWarpFactor,
    getFuelEfficiencyBonus,
    currentFuel,
    maxFuel,
    starSystems,
    calculateDistanceBetweenSystems,
    navigationMode
  } = params;

  // Calculate fuel needed for this trip using the ORIGIN location
  const origin = originLocation || currentLocation;
  const distance = NavUtils.calculateDistance(origin, selectedSystem, distancePositions);
  const warpFactor = selectedWarpFactor || getDefaultWarpFactor();

  // Apply fuel efficiency from upgrades (reduces consumption)
  const upgradeBonuses = { fuelEfficiency: getFuelEfficiencyBonus() };

  const fuelNeeded = NavUtils.calculateFuelNeeded(distance, warpFactor, upgradeBonuses.fuelEfficiency);

  const fuelConsumptionRate = fuelNeeded / travelTime; // Fuel per second

  // CRITICAL: Verify fuelConsumptionRate is valid
  if (fuelConsumptionRate === 0 || isNaN(fuelConsumptionRate)) {
    console.error('[WarpTravelManager] ERROR: Invalid fuel consumption rate!', {
      distance,
      warpFactor,
      travelTime,
      fuelNeeded,
      calculation: `${fuelNeeded} / ${travelTime} = ${fuelConsumptionRate}`
    });
  }

  // Check if we have enough fuel
  if (currentFuel < fuelNeeded) {
    // Try to find a warp factor that works with current fuel to reach destination
    let recommendedWarp = null;
    for (let testWarp = 1; testWarp <= 9; testWarp++) {
      const testFuelNeeded = NavUtils.calculateFuelNeeded(distance, testWarp, upgradeBonuses.fuelEfficiency);
      if (testFuelNeeded <= currentFuel) {
        recommendedWarp = testWarp;
      }
    }

    // Find intermediate destinations we CAN reach that are CLOSER to the target
    const currentDistanceToTarget = distance;

    const reachableStops = starSystems
      .filter(system => system.id !== selectedSystem.id && system.id !== origin.id)
      .map(system => {
        const stopDistance = NavUtils.calculateDistance(origin, system, distancePositions);

        // Find the best warp factor we can afford for this stop
        let bestWarp = null;
        let bestWarpFuel = Infinity;

        for (let testWarp = 9; testWarp >= 1; testWarp--) {
          const testFuelNeeded = NavUtils.calculateFuelNeeded(stopDistance, testWarp, upgradeBonuses.fuelEfficiency);
          if (testFuelNeeded <= currentFuel) {
            bestWarp = testWarp;
            bestWarpFuel = testFuelNeeded;
            break; // Found the highest warp we can afford
          }
        }

        // If no warp factor works, try Warp 1 anyway to get the minimum fuel needed
        if (bestWarp === null) {
          bestWarp = 1;
          bestWarpFuel = NavUtils.calculateFuelNeeded(stopDistance, 1, upgradeBonuses.fuelEfficiency);
        }

        // Calculate distance from this stop to the final destination
        const distanceFromStopToTarget = calculateDistanceBetweenSystems(system, selectedSystem);

        // Check if this stop gets us closer to the target
        const getsUsCloser = distanceFromStopToTarget < currentDistanceToTarget;

        // Calculate how much progress we make toward the target
        const progressToTarget = currentDistanceToTarget - distanceFromStopToTarget;

        const canReach = bestWarpFuel <= currentFuel;

        return {
          system,
          distance: stopDistance,
          bestWarp: bestWarp,
          fuelNeeded: bestWarpFuel,
          canReach: canReach,
          distanceToTarget: distanceFromStopToTarget,
          progressToTarget: progressToTarget,
          getsUsCloser: getsUsCloser
        };
      })
      .filter(stop => {
        // STRICT FILTER: Only show stops that are ACTUALLY reachable AND get us closer
        const isReachable = stop.canReach && stop.fuelNeeded <= currentFuel;
        const makesProgress = stop.getsUsCloser && stop.progressToTarget > 0;
        return isReachable && makesProgress;
      })
      .sort((a, b) => {
        // Sort by most progress toward target (biggest reduction in distance)
        return b.progressToTarget - a.progressToTarget;
      });

    // Build warning message
    const fuelPercentOfMax = Math.round((currentFuel / maxFuel) * 100);
    const fuelWithoutMargin = Math.ceil(fuelNeeded / 1.1);
    const safetyMargin = fuelNeeded - fuelWithoutMargin;

    let warningMessage = `Need ${Math.round(fuelNeeded)}% fuel for ${Math.round(distance)} LY journey at Warp ${warpFactor}.\n(Base: ${fuelWithoutMargin}% + Safety margin: ${safetyMargin}%)\n\nCurrent fuel: ${Math.round(currentFuel)}% (${fuelPercentOfMax}% of ${maxFuel}% capacity)`;

    if (recommendedWarp) {
      const recommendedFuelNeeded = NavUtils.calculateFuelNeeded(distance, recommendedWarp, upgradeBonuses.fuelEfficiency);
      warningMessage += `\n\n💡 RECOMMENDATION: Reduce to Warp ${recommendedWarp} (requires ${Math.round(recommendedFuelNeeded)}% fuel)`;
    } else if (reachableStops.length > 0) {
      warningMessage += `\n\n🚫 DESTINATION TOO FAR: Even at full capacity (${maxFuel}%), you cannot reach this destination.`;
      warningMessage += `\n\n💡 TIP: Upgrade fuel tanks at space stations to increase range!`;
      warningMessage += `\n\n🛰️ SUGGESTED STOPS (toward ${selectedSystem.name}):`;
      reachableStops.slice(0, 3).forEach(stop => {
        warningMessage += `\n• ${stop.system.name} (${Math.round(stop.distance)} LY @ Warp ${stop.bestWarp}, ${Math.round(stop.fuelNeeded)}% fuel) → ${Math.round(stop.distanceToTarget)} LY to target`;
      });
    } else {
      warningMessage += `\n\n🚫 DESTINATION TOO FAR: No warp factor can reach this destination with current fuel. No intermediate stops available - find a fuel depot!`;
    }

    // Return insufficient fuel event data
    return {
      type: 'INSUFFICIENT_FUEL',
      warningMessage,
      recommendedWarp,
      reachableStops,
      distance,
      fuelNeeded,
      upgradeBonuses,
      navigationMode
    };
  }

  // If we have enough fuel, return success with fuel consumption rate
  return {
    type: 'START_TRAVEL',
    fuelConsumptionRate,
    travelTime
  };
};

/**
 * Calculate position interpolation during warp travel
 * 
 * @param {Object} fromPos - Starting position {x, y, z}
 * @param {Object} toPos - Destination position {x, y, z}
 * @param {number} progressPercent - Progress as decimal (0.0 to 1.0)
 * @returns {Object} Current position {x, y, z}
 */
export const calculateWarpPosition = (fromPos, toPos, progressPercent) => {
  if (!fromPos || !toPos) return null;

  return {
    x: fromPos.x + (toPos.x - fromPos.x) * progressPercent,
    y: fromPos.y + (toPos.y - fromPos.y) * progressPercent,
    z: (fromPos.z || 0) + ((toPos.z || 0) - (fromPos.z || 0)) * progressPercent
  };
};

/**
 * Check for engine damage at high warp factors
 * 
 * @param {number} warpFactor - Current warp factor (1-9)
 * @param {number} progress - Travel progress percentage (0-100)
 * @param {boolean} alreadyChecked - Whether damage has already been checked this trip
 * @returns {Object|null} Damage result or null if no damage
 */
export const checkEngineDamage = (warpFactor, progress, alreadyChecked) => {
  // Only check once per trip at configured progress point
  if (alreadyChecked || progress < GameConfig.warp.warp8.checkProgressPercent) {
    return null;
  }

  if (warpFactor === 8) {
    const warp8Config = GameConfig.warp.warp8;
    const damageChance = getDifficultyMultipliers().warp8DamageChance;
    if (Math.random() < damageChance) {
      const damageRange = warp8Config.maxDamage - warp8Config.minDamage + 1;
      const damage = Math.floor(Math.random() * damageRange) + warp8Config.minDamage;
      return {
        damage,
        message: `⚠️ WARP 8 STRAIN: Engines damaged ${damage} HP from high-speed stress!`,
        color: '#ffaa00'
      };
    }
  } else if (warpFactor === 9) {
    const warp9Config = GameConfig.warp.warp9;
    const damageChance = getDifficultyMultipliers().warp9DamageChance;
    if (Math.random() < damageChance) {
      const damageRange = warp9Config.maxDamage - warp9Config.minDamage + 1;
      const damage = Math.floor(Math.random() * damageRange) + warp9Config.minDamage;
      return {
        damage,
        message: `💥 WARP 9 CRITICAL STRAIN: Engines damaged ${damage} HP from extreme velocity!`,
        color: '#ff4444'
      };
    }
  }

  return null;
};

/**
 * Find nearest star system from a given position
 * 
 * @param {Object} position - Current position {x, y, z}
 * @param {Array} starSystems - All available star systems
 * @param {Object} distancePositions - Position lookup for all systems
 * @returns {Object} Nearest star system
 */
export const findNearestSystem = (position, starSystems, distancePositions) => {
  // Guard against null position
  if (!position || position.x === undefined || position.y === undefined) {
    console.error('[WarpTravelManager] Invalid position for findNearestSystem');
    // Return first system with valid position as fallback
    for (const system of starSystems) {
      const systemPos = distancePositions[system.id];
      if (systemPos && systemPos.x !== undefined && systemPos.y !== undefined) {
        return system;
      }
    }
    throw new Error('No valid systems available');
  }
  
  let nearestStar = null;
  let shortestDistance = Infinity;

  starSystems.forEach(system => {
    const systemPos = distancePositions[system.id];
    
    // Skip systems without valid position data
    if (!systemPos || systemPos.x === undefined || systemPos.y === undefined) {
      return;
    }

    // Calculate actual distance from position to this system
    const dx = systemPos.x - position.x;
    const dy = systemPos.y - position.y;
    const distToSystem = Math.sqrt(dx * dx + dy * dy);

    if (distToSystem < shortestDistance) {
      shortestDistance = distToSystem;
      nearestStar = system;
    }
  });

  // Fallback: find first system with valid position
  if (!nearestStar) {
    for (const system of starSystems) {
      const systemPos = distancePositions[system.id];
      if (systemPos && systemPos.x !== undefined && systemPos.y !== undefined) {
        nearestStar = system;
        break;
      }
    }
  }

  // Final fallback: use first system even without position check
  if (!nearestStar && starSystems.length > 0) {
    nearestStar = starSystems[0];
  }

  if (!nearestStar) {
    throw new Error('No star systems available');
  }

  return nearestStar;
};

/**
 * Create stranded location object
 * 
 * @param {Object} position - Stranded position {x, y, z}
 * @param {number} distanceTraveled - Distance traveled before fuel depletion
 * @param {Object} originLocation - Starting location
 * @returns {Object} Stranded location object
 */
export const createStrandedLocation = (position, distanceTraveled, originLocation) => {
  return {
    id: -1,
    name: `DEEP SPACE (${distanceTraveled} LY from ${originLocation.name})`,
    threat: 'UNKNOWN',
    resources: 'NONE',
    x: position.x,
    y: position.y,
    z: position.z
  };
};

/**
 * Create tow offer event data
 * 
 * @param {Object} nearestStar - Nearest star system for tow
 * @param {number} distanceTraveled - Distance traveled before fuel depletion
 * @param {number} distanceRemaining - Distance remaining to original destination
 * @param {Object} originalDestination - Original destination system
 * @returns {Object} Tow offer data
 */
export const createTowOfferData = (nearestStar, distanceTraveled, distanceRemaining, originalDestination) => {
  const towRefuelPercent = GameConfig.credits.towRefuelPercent;

  return {
    nearestStar,
    distanceTraveled,
    distanceRemaining,
    originalDestination,
    towRefuelPercent
  };
};
